import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { AppState } from '$/store';
import ResultsCounter from '$/components/resultsCounter';
import Loading from '$/components/loading/Loading';
import PrintPage from '$/components/printPage';
import TranscriptionItem from '$/components/transcriptionItem';
import { AnalyticsService, TranscriptionsClientService } from '$/services';
import { downloadClientTranscriptionAudio, downloadClientTranscriptionText } from '$/store/export/actions';
import { toastError } from '$/store/toast/actions';
import * as Bixby from '$/core';
import NotFound from '$/pages/not-found';
import { CLIENT_COMPANY_TRANSCRIPTIONS_VIEW } from '$/core/constants';
import { getIsCurrentPortfolioCompanyPublicState } from '$/store/portfolio/selectors';
import './index.scss';

interface ICompanyTranscriptionsProps {
  currentCompany?: Bixby.Models.Company;
  profile?: Bixby.Models.Profile;
  featureFlags?: string[];
  isCurrentPortfolioCompanyPublic: boolean;
  dispatchDownloadTranscriptAudio: (id: string, companyName: string) => Promise<void>;
  dispatchDownloadTranscriptText: (id: string, companyName: string) => Promise<void>;
  dispatchRetrieveTranscriptTextToastFailure: () => Promise<void>;
}

const CompanyTranscriptions: React.FC<ICompanyTranscriptionsProps> = props => {
  const {
    currentCompany,
    profile,
    isCurrentPortfolioCompanyPublic,
    dispatchDownloadTranscriptAudio,
    dispatchDownloadTranscriptText,
    dispatchRetrieveTranscriptTextToastFailure,
  } = props;

  const featureFlags = useMemo(() => {
    return props.featureFlags as string[];
  }, [props.featureFlags]);
  const [isLoading, setIsLoading] = useState(false);
  const [transcriptionsError, setTranscriptionsError] = useState(undefined as string | undefined);
  const [publicTranscriptions, setPublicTranscriptions] = useState([] as Bixby.Models.ClientCompanyTranscription[]);
  const [privateTranscriptions, setPrivateTranscriptions] = useState([] as Bixby.Models.ClientCompanyTranscription[]);
  const [activeFilterTypes] = useState([] as Array<'public' | 'private'>);

  const handleRetrieveTranscriptText: (transcriptionId: string) => Promise<string> = async (
    transcriptionId: string
  ) => {
    handleTranscriptionEvent(Bixby.Constants.APPINSIGHTS_TRANSCRIPTIONS_CLIENT_TEXT_RETRIEVE, transcriptionId);
    return await TranscriptionsClientService.retrieveTranscriptionText(transcriptionId);
  };

  const handleRetrieveTranscriptTextFailure = () => dispatchRetrieveTranscriptTextToastFailure();

  const handleDownloadTranscriptAudio = (transcriptionId: string, title: string) => {
    handleTranscriptionEvent(Bixby.Constants.APPINSIGHTS_TRANSCRIPTIONS_CLIENT_AUDIO_DOWNLOAD, transcriptionId);
    dispatchDownloadTranscriptAudio(transcriptionId, title);
  };

  const handleDownloadTranscriptText = (transcriptionId: string, title: string) => {
    handleTranscriptionEvent(Bixby.Constants.APPINSIGHTS_TRANSCRIPTIONS_CLIENT_TEXT_DOWNLOAD, transcriptionId);
    dispatchDownloadTranscriptText(transcriptionId, title);
  };

  const handleTranscriptionEvent = (eventName: string, transcriptionId: string) => {
    if (profile) {
      AnalyticsService.event(eventName, {
        ...profile,
        companyId: currentCompany?.id,
        companyName: currentCompany?.name,
        transcriptionId: transcriptionId,
      });
    }
  };

  useEffect(() => {
    const getTranscriptionsAsync = async () => {
      setIsLoading(true);
      setTranscriptionsError(undefined);
      try {
        if (!currentCompany) return;

        const transcriptions: Bixby.Models.ClientCompanyTranscription[] =
          await TranscriptionsClientService.getTranscriptionsByCompanyId(currentCompany?.id ?? '');

        if (currentCompany.declarationType === Bixby.Enums.DeclarationType.Public) {
          setPublicTranscriptions(transcriptions);
        } else {
          setPrivateTranscriptions(transcriptions.filter(_ => _.hasPrivateDeclaration));
          setPublicTranscriptions(transcriptions.filter(_ => !_.hasPrivateDeclaration));
        }
      } catch (e: any) {
        setTranscriptionsError('Error retrieving transcriptions');
      } finally {
        setIsLoading(false);
      }
    };

    if (
      featureFlags.includes(CLIENT_COMPANY_TRANSCRIPTIONS_VIEW) &&
      !Bixby.Utils.isNullOrEmpty(currentCompany) &&
      isCurrentPortfolioCompanyPublic === false
    ) {
      getTranscriptionsAsync();
    }
  }, [featureFlags, currentCompany, isCurrentPortfolioCompanyPublic]);

  let feedbackUrl = '';

  if (currentCompany) {
    const companyNameSanitized: string = (currentCompany.name || '').replace('&', '%26');
    const dataSiteNameSanitized: string = (currentCompany.dataSiteName || '').replace('&', '%26');

    const feedbackUrlSubject = `Transcript%20Request%20-%20${companyNameSanitized}`;
    const dataSite: string = dataSiteNameSanitized ? `%20%28${dataSiteNameSanitized}%29` : '';
    const feedbackUrlBody = `Please%20add%20Transcript%20for%20${companyNameSanitized}${dataSite}%2c%20and%20include%20any%20specific%20requests%20here%3A%0D%0A%0D%0A`;
    feedbackUrl = `mailto:feedback@bixbyresearch.com?subject=${feedbackUrlSubject}&body=${feedbackUrlBody}`;
  }

  const handleFeedbackClick = () => {
    if (profile && currentCompany) {
      AnalyticsService.event(Bixby.Constants.APPINSIGHTS_FEEDBACK_LINK_CLICK, {
        ...profile,
        type: 'Transcripts/Replays',
        companyName: currentCompany.name,
      });
    }
  };

  const filteredTranscriptions = useMemo(() => {
    if (Bixby.Utils.isNullOrEmpty(publicTranscriptions) && Bixby.Utils.isNullOrEmpty(privateTranscriptions)) {
      return [];
    } else if (activeFilterTypes.length === 0) {
      return [...publicTranscriptions, ...privateTranscriptions];
    }

    const filtered = [] as Bixby.Models.ClientCompanyTranscription[];
    if (activeFilterTypes.includes('public')) {
      filtered.push(...publicTranscriptions);
    }
    if (activeFilterTypes.includes('private')) {
      filtered.push(...privateTranscriptions);
    }

    return filtered;
  }, [publicTranscriptions, privateTranscriptions, activeFilterTypes]);

  if (!featureFlags.includes(CLIENT_COMPANY_TRANSCRIPTIONS_VIEW)) {
    return <NotFound />;
  } else if (!Bixby.Utils.isNullOrEmpty(transcriptionsError)) {
    return <p className="error">{transcriptionsError}</p>;
  } else if (Bixby.Utils.isNullOrEmpty(currentCompany)) {
    return null;
  } else if (isLoading) {
    return <Loading pageLevel />;
  } else if (isCurrentPortfolioCompanyPublic) {
    return (
      <div className="company_transcriptions">
        <p className="note_contain">
          Transcripts not available for public companies.{' '}
          <a href="mailto:feedback@bixbyresearch.com" role="button" onClick={handleFeedbackClick}>
            Click here
          </a>{' '}
          to provide feedback to Bixby.
        </p>
      </div>
    );
  } else {
    return (
      <React.Fragment>
        <div className="company_transcriptions">
          <PrintPage />
          <ResultsCounter amount={(filteredTranscriptions || []).length} />
          <div className="note_contain">
            Transcripts/Replays
            <div className="note">
              <p>
                Don't see a call transcript? Need a DRAFT transcript reviewed/converted to FINAL?&nbsp;
                <a href={feedbackUrl} role="button" onClick={handleFeedbackClick}>
                  Click here
                </a>
                &nbsp;to have it prioritized.
              </p>
              <p>FINAL - subsequent review to correct for identifiable audio translation errors</p>
            </div>
          </div>
          {(filteredTranscriptions || []).map((t, ix) => {
            return (
              <TranscriptionItem
                key={ix + '-' + t.transcriptionId}
                id={t.transcriptionId}
                companyName={t.companyName}
                title={t.newsLenderSubject}
                date={t.newsLenderStartDate}
                aboutUrl={`/company/${t.companyId}`}
                aboutTitle={t.companyName}
                status={t.transcriptionStatus}
                isPrivateMNPI={t.hasPrivateDeclaration}
                handleDownloadTranscriptAudio={handleDownloadTranscriptAudio}
                handleDownloadTranscriptText={handleDownloadTranscriptText}
                handleRetrieveTranscriptText={handleRetrieveTranscriptText}
                handleRetrieveTranscriptTextFailure={handleRetrieveTranscriptTextFailure}
              />
            );
          })}
        </div>
      </React.Fragment>
    );
  }
};

const mapStateToProps = (state: AppState) => {
  const { isCurrentPortfolioCompanyPublic } = getIsCurrentPortfolioCompanyPublicState(state);
  return {
    isCurrentPortfolioCompanyPublic,
    currentCompany: state.portfolioState.currentCompany,
    isLoading: state.portfolioState.isLoading,
    error: state.portfolioState.error,
    profile: state.profileState.profile,
    featureFlags: state.loginState.featureFlags,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchDownloadTranscriptAudio: (id: string, title: string) => dispatch(downloadClientTranscriptionAudio(id, title)),
  dispatchDownloadTranscriptText: (id: string, title: string) => dispatch(downloadClientTranscriptionText(id, title)),
  dispatchRetrieveTranscriptTextToastFailure: () => dispatch(toastError('Failed to download transcript')),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyTranscriptions);
